"use client";

import type { ChatStarter, RestrictableKind } from "@repo/embed";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@ui/lib";
import {
	ChevronRightIcon,
	HeadsetIcon,
	ShoppingBasketIcon,
} from "lucide-react";
import { getStarterPhotosFn, type ShopperPageData } from "~/server/shopper";

/**
 * The first screen: one question and the
 * retailer's suggested first messages as tiles. Each tile carries photos of
 * what that message would find in this store's catalog, so the shopper sees
 * the assistant knows the shelves before they type anything. The retailer can
 * write the question and a line under it, and show the suggestions as a plain
 * list instead (`chat` settings).
 */
export function Welcome({
	page,
	appStarters,
	restrict,
	welcomeTitle,
	onPick,
	onPress,
	onSupport,
	supportLabel,
}: {
	/** "Help with an order", or the retailer's wording for it. */
	supportLabel: string;
	page: ShopperPageData;
	/** The app's suggestions (`starters`), shown once checked; null: the retailer's own. */
	appStarters: ChatStarter[] | null;
	/** What the app added to the retailer's restrictions: its suggestions are checked against both. */
	restrict: RestrictableKind[];
	/** The built-in question in the chat's language, when the retailer wrote none. */
	welcomeTitle: string;
	onPick: (text: string) => boolean;
	/** The finger is down: the search can start before the tap lands. */
	onPress: (text: string) => void;
	onSupport: (() => void) | null;
}) {
	const photosStyle = page.chat.starterStyle === "photos";
	// The app's suggestions show once the server has checked them (and found their photos).
	const app = useQuery({
		enabled: page.chat.showStarters && appStarters !== null,
		queryKey: [
			"shopper",
			"app-starters",
			page.orgSlug,
			page.storeId,
			appStarters,
			restrict,
		],
		queryFn: () => appStartersFor(page, appStarters ?? [], restrict),
		staleTime: 10 * 60_000,
	});
	// `chat.showStarters` off: the question and the message box, nothing to tap.
	const starters: ChatStarter[] = !page.chat.showStarters
		? []
		: appStarters
			? (app.data?.starters ?? [])
			: page.starterPrompts.map((label) => ({ label }));
	const withPhotos = photosStyle && starters.length > 0;
	const retailerPhotos = useQuery({
		enabled: withPhotos && appStarters === null,
		queryKey: ["shopper", "starter-photos", page.orgSlug, page.storeId],
		queryFn: () =>
			getStarterPhotosFn({
				data: { orgSlug: page.orgSlug, store: page.storeId },
			}),
		staleTime: 10 * 60_000,
	});
	const photos = appStarters ? app : retailerPhotos;
	const photoOf = (prompt: string) =>
		appStarters ? app.data?.photos[prompt] : retailerPhotos.data?.[prompt];
	return (
		<div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
			<div
				aria-hidden
				className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(110%_100%_at_50%_0%,var(--shop-brand-soft)_0%,transparent_70%)]"
			/>
			<div className="relative mx-auto flex w-full max-w-2xl flex-col gap-6 px-4 pt-7 pb-5">
				<div className="flex animate-shop-rise flex-col gap-2 px-1">
					<h1
						dir="auto"
						className="text-balance font-extrabold text-(--shop-ink) text-[2rem] leading-[1.05] tracking-[-0.035em]"
					>
						{page.chat.welcomeTitle.trim() || welcomeTitle}
					</h1>
					{page.chat.welcomeSubtitle.trim() ? (
						<p
							dir="auto"
							className="text-pretty text-(--shop-muted) text-[15px] leading-snug"
						>
							{page.chat.welcomeSubtitle}
						</p>
					) : null}
				</div>

				<ul
					className={cn(
						withPhotos
							? "grid grid-cols-2 gap-2.5"
							: "flex flex-col gap-2",
						!starters.length && "hidden",
					)}
				>
					{starters.map(({ label, prompt = label }, index) => (
						<li
							key={label}
							className={cn(
								withPhotos &&
									index === starters.length - 1 &&
									starters.length % 2 === 1 &&
									"col-span-2",
							)}
						>
							<button
								type="button"
								dir="auto"
								onPointerDown={() => onPress(prompt)}
								onClick={() => onPick(prompt)}
								style={{
									animationDelay: `${60 + index * 45}ms`,
								}}
								className={cn(
									"press group flex w-full animate-shop-rise bg-(--shop-card) text-start shadow-[0_1px_2px_rgb(0_0_40/0.05)] outline-none ring-1 ring-(--shop-brand-line)/80 transition-shadow hover:ring-(--shop-brand-line) hover:shadow-[0_6px_20px_-8px_rgb(0_0_40/0.18)] focus-visible:ring-3 focus-visible:ring-(--shop-brand)/35",
									withPhotos
										? "h-full flex-col items-start gap-3.5 rounded-[calc(var(--radius)*3)] p-3"
										: "items-center gap-3 rounded-[calc(var(--radius)*2.5)] px-4 py-3",
								)}
							>
								{withPhotos ? (
									<PhotoStack
										photos={photoOf(prompt)}
										loading={photos.isPending}
									/>
								) : null}
								<span
									className={cn(
										"w-full font-semibold text-(--shop-ink) text-[14.5px] leading-snug",
										withPhotos
											? "line-clamp-3"
											: "line-clamp-2 flex-1",
									)}
								>
									{/* French "parfums ?": the mark never wraps alone. */}
									{label.replace(/ ([?!:;»])/g, "\u00a0$1")}
								</span>
								{withPhotos ? null : (
									<ChevronRightIcon
										className="size-4 shrink-0 text-(--shop-muted) rtl:rotate-180"
										aria-hidden
									/>
								)}
							</button>
						</li>
					))}
				</ul>

				{onSupport ? (
					<button
						type="button"
						onClick={onSupport}
						className="press -mt-2 flex w-full items-center gap-3 rounded-(--shop-btn) py-2 ps-1.5 pe-3 text-start outline-none hover:bg-(--shop-brand-soft) focus-visible:ring-3 focus-visible:ring-(--shop-brand)/35"
					>
						<span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-(--shop-card) text-(--shop-brand-text) ring-1 ring-(--shop-brand-line)">
							<HeadsetIcon className="size-4" aria-hidden />
						</span>
						<span className="min-w-0 flex-1 font-semibold text-(--shop-ink) text-sm">
							{supportLabel}
						</span>
						<ChevronRightIcon
							className="size-4 shrink-0 text-(--shop-muted) rtl:rotate-180"
							aria-hidden
						/>
					</button>
				) : null}
			</div>
		</div>
	);
}

/** Up to three catalog photos, fanned like products picked off a shelf. */
function PhotoStack({
	photos,
	loading,
}: {
	photos: string[] | undefined;
	loading: boolean;
}) {
	if (loading) {
		return (
			<span aria-hidden className="flex">
				{[0, 1, 2].map((i) => (
					<span
						key={i}
						className="-ms-4 size-12 animate-pulse rounded-2xl bg-(--shop-brand-soft) ring-2 ring-(--shop-card) first:ms-0"
					/>
				))}
			</span>
		);
	}
	if (!photos?.length) {
		return (
			<span
				aria-hidden
				className="flex size-12 items-center justify-center rounded-2xl bg-(--shop-brand-soft) text-(--shop-brand-text)"
			>
				<ShoppingBasketIcon className="size-5" />
			</span>
		);
	}
	return (
		<span aria-hidden className="flex ps-0.5 pt-0.5">
			{photos.map((src, i) => (
				<span
					key={src}
					style={{ zIndex: photos.length - i }}
					className={cn(
						"-ms-4 flex size-12 animate-shop-rise items-center justify-center overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_rgb(0_0_40/0.06),0_2px_8px_-2px_rgb(0_0_40/0.2)] transition-transform first:ms-0",
						FAN[i],
					)}
				>
					<img
						src={src}
						alt=""
						loading="lazy"
						decoding="async"
						className="size-full object-contain p-1"
					/>
				</span>
			))}
		</span>
	);
}

/** Resting tilt per photo, and how it fans out when the tile is hovered. */
const FAN = [
	"-rotate-6 group-hover:-rotate-9",
	"rotate-3 group-hover:translate-x-1 group-hover:rotate-6",
	"rotate-9 group-hover:translate-x-2 group-hover:rotate-12",
];

/** The app's suggestions as the server lets them show (checked, with photos); none on failure. */
async function appStartersFor(
	page: ShopperPageData,
	starters: ChatStarter[],
	restrict: RestrictableKind[],
): Promise<{ starters: ChatStarter[]; photos: Record<string, string[]> }> {
	const res = await fetch(`/api/public/${page.orgSlug}/starters`, {
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify({
			starters,
			restrict,
			...(page.storeId ? { store: page.storeId } : {}),
		}),
	});
	return res.ok ? res.json() : { starters: [], photos: {} };
}
